import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_DETAILS, AUTH_TOKEN, MENU_ITEMS } from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import AuthService from 'services/AuthService';
 
export const initialState = {
    loading: false,
    message: '',
    showMessage: false,
    redirect: '',
    token: localStorage.getItem(AUTH_TOKEN) || null,
    auth_details: localStorage.getItem(AUTH_DETAILS) || null,
    menu_items: localStorage.getItem(MENU_ITEMS) || null,
}

/** Safe parsed auth details (null if missing or invalid). Use in components instead of JSON.parse(state.auth.auth_details). */
export const selectAuthDetails = (state) => {
    const d = state.auth?.auth_details
    if (d == null || d === '') return null
    try {
        return typeof d === 'object' ? d : JSON.parse(d)
    } catch {
        return null
    }
}
 
export const signIn = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
    const { email, password } = data
    try {
        const response = await AuthService.login({ email, password })
        const payload = response?.data !== undefined ? response.data : response
        if (!payload || !payload.token) {
            return rejectWithValue('Invalid login response')
        }
        const token = payload.token
        localStorage.setItem(AUTH_TOKEN, token)
        localStorage.setItem(MENU_ITEMS, JSON.stringify(payload.permissions || []))
        localStorage.setItem(AUTH_DETAILS, JSON.stringify(payload))
        return payload
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})
 
export const signUp = createAsyncThunk('auth/register', async (data, { rejectWithValue }) => {
    try {
        const response = await AuthService.register(data)
        const payload = response?.data !== undefined ? response.data : response
        if (!payload || !payload.token) {
            return rejectWithValue('Invalid register response')
        }
        const token = payload.token
        localStorage.setItem(AUTH_TOKEN, token)
        localStorage.setItem(MENU_ITEMS, JSON.stringify(payload.permissions || []))
        localStorage.setItem(AUTH_DETAILS, JSON.stringify(payload))
        return payload
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})
 
export const signOut = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
    try {
        await FirebaseService.signOutRequest()
        try {
            await AuthService.logout()
        } catch (e) {
            // ignore if backend logout fails (e.g. token already invalid)
        }
        localStorage.removeItem(AUTH_TOKEN)
        localStorage.removeItem(AUTH_DETAILS)
        localStorage.removeItem(MENU_ITEMS)
        return null
    } catch (err) {
        localStorage.removeItem(AUTH_TOKEN)
        localStorage.removeItem(AUTH_DETAILS)
        localStorage.removeItem(MENU_ITEMS)
        return rejectWithValue(err?.message || 'Error')
    }
})
 
export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
    try {
        const response = await AuthService.loginInOAuth()
        const token = response.data.token;
        localStorage.setItem(AUTH_TOKEN, token);
        return token;
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})
 
export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
    try {
        const response = await AuthService.loginInOAuth()
        const token = response.data.token;
        localStorage.setItem(AUTH_TOKEN, token);
        return token;
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const fetchAuthUser = createAsyncThunk('auth/fetchUser', async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await AuthService.getMe()
        const payload = response?.data !== undefined ? response.data : response
        if (!payload) return rejectWithValue('No user data')
        dispatch(setAuthUser(payload))
        // Persist to localStorage so permissions survive refresh and stay in sync
        localStorage.setItem(AUTH_DETAILS, JSON.stringify(payload))
        localStorage.setItem(MENU_ITEMS, JSON.stringify(payload.permissions || []))
        return payload
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || err?.message || 'Error')
    }
})
 
 
export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        authenticated: (state, action) => {
            state.loading = false
            state.redirect = '/'
            state.token = action.payload
        },
        showAuthMessage: (state, action) => {
            state.message = action.payload
            state.showMessage = true
            state.loading = false
        },
        hideAuthMessage: (state) => {
            state.message = ''
            state.showMessage = false
        },
        signOutSuccess: (state) => {
            state.loading = false
            state.token = null
            state.auth_details = null
            state.menu_items = null
            state.redirect = '/'
        },
        setAuthUser: (state, action) => {
            const payload = action.payload
            if (payload == null) return
            state.auth_details = typeof payload === 'string' ? payload : JSON.stringify(payload)
            state.menu_items = typeof payload === 'object' && typeof payload.permissions !== 'undefined'
                ? JSON.stringify(payload.permissions)
                : (state.menu_items ?? null)
        },
        showLoading: (state) => {
            state.loading = true
        },
        signInSuccess: (state, action) => {
            state.loading = false
            state.token = action.payload
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(signIn.pending, (state) => {
                state.loading = true
            })
            .addCase(signIn.fulfilled, (state, action) => {
                state.loading = false
                state.redirect = '/'
                const payload = action.payload
                if (payload) {
                    state.token = payload.token || state.token
                    state.auth_details = typeof payload === 'string' ? payload : JSON.stringify(payload)
                    state.menu_items = typeof payload.permissions !== 'undefined'
                        ? JSON.stringify(payload.permissions)
                        : (state.menu_items || null)
                }
            })
            .addCase(signIn.rejected, (state, action) => {
                state.message = action.payload
                state.showMessage = true
                state.loading = false
            })
            .addCase(signOut.fulfilled, (state) => {
                state.loading = false
                state.token = null
                state.auth_details = null
                state.menu_items = null
                state.redirect = '/'
            })
            .addCase(signOut.rejected, (state) => {
                state.loading = false
                state.token = null
                state.auth_details = null
                state.menu_items = null
                state.redirect = '/'
            })
            .addCase(signUp.pending, (state) => {
                state.loading = true
            })
            .addCase(signUp.fulfilled, (state, action) => {
                state.loading = false
                state.redirect = '/'
                const payload = action.payload
                if (payload) {
                    state.token = payload.token || state.token
                    state.auth_details = typeof payload === 'string' ? payload : JSON.stringify(payload)
                    state.menu_items = typeof payload.permissions !== 'undefined'
                        ? JSON.stringify(payload.permissions)
                        : (state.menu_items || null)
                }
            })
            .addCase(signUp.rejected, (state, action) => {
                state.message = action.payload
                state.showMessage = true
                state.loading = false
            })
            .addCase(signInWithGoogle.pending, (state) => {
                state.loading = true
            })
            .addCase(signInWithGoogle.fulfilled, (state, action) => {
                state.loading = false
                state.redirect = '/'
                state.token = action.payload
            })
            .addCase(signInWithGoogle.rejected, (state, action) => {
                state.message = action.payload
                state.showMessage = true
                state.loading = false
            })
            .addCase(signInWithFacebook.pending, (state) => {
                state.loading = true
            })
            .addCase(signInWithFacebook.fulfilled, (state, action) => {
                state.loading = false
                state.redirect = '/'
                state.token = action.payload
            })
            .addCase(signInWithFacebook.rejected, (state, action) => {
                state.message = action.payload
                state.showMessage = true
                state.loading = false
            })
    },
})
 
export const {
    authenticated,
    showAuthMessage,
    hideAuthMessage,
    signOutSuccess,
    showLoading,
    signInSuccess,
    setAuthUser,
} = authSlice.actions
 
export default authSlice.reducer